import { useState } from "react";
import { useMySubTasks } from "@/features/tasks/hooks/useSubTasks";
import { Spinner } from "@/components/ui/Spinner";
import { SubTaskBoardCard } from "@/features/tasks/components/SubTaskBoardCard";
import { SubTaskDetailPanel } from "@/features/tasks/components/SubTaskDetailPanel";
import {
  TASK_STATUS_LABELS,
  type TaskStatus,
} from "@/features/tasks/types";
import { useAuthStore } from "@/store/auth";

const ACTIVE_STATUSES: TaskStatus[] = ["todo_now", "in_progress", "todo_later", "waiting"];

export function MyTasksPage() {
  const { data: subtasks = [], isLoading } = useMySubTasks();
  const [selectedSubTask, setSelectedSubTask] = useState<{ taskId: string; subtaskId: string } | null>(null);
  const user = useAuthStore((s) => s.user);

  const byStatus = ACTIVE_STATUSES.reduce<Record<TaskStatus, typeof subtasks>>(
    (acc, s) => ({ ...acc, [s]: [] }),
    {} as Record<TaskStatus, typeof subtasks>
  );
  subtasks.forEach((st) => {
    if (byStatus[st.status]) byStatus[st.status].push(st);
  });

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Moje zadania</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          {user?.first_name} {user?.last_name} · {subtasks.length} aktywnych
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : subtasks.length === 0 ? (
        <div className="text-center py-16 text-gray-400 dark:text-gray-500">
          <p className="text-lg mb-2">Brak aktywnych podzadań</p>
          <p className="text-sm">Wszystko zrobione!</p>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {ACTIVE_STATUSES.map((status) => {
            const group = byStatus[status];
            if (group.length === 0) return null;
            return (
              <div key={status}>
                <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-3">
                  {TASK_STATUS_LABELS[status]} ({group.length})
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {group.map((st) => (
                    <SubTaskBoardCard
                      key={st.id}
                      subtask={st}
                      onClick={() => setSelectedSubTask({ taskId: st.task, subtaskId: st.id })}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedSubTask && (
        <SubTaskDetailPanel
          taskId={selectedSubTask.taskId}
          subtaskId={selectedSubTask.subtaskId}
          onClose={() => setSelectedSubTask(null)}
        />
      )}
    </div>
  );
}
